/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosResponse, } from 'axios';
import FormData from 'form-data';
import { QueryClient } from '@tanstack/react-query';

// import errors can be ignored as this file will be copied to different location
import { ApiError } from '../../api/core/ApiError';
import type { ApiRequestOptions } from '../../api/core/ApiRequestOptions';
import type { ApiResult } from '../../api/core/ApiResult';
import type { OnCancel } from '../../api/core/CancelablePromise';
import { CancelablePromise } from '../../api/core/CancelablePromise';
import type { OpenAPIConfig } from '../../api/core/OpenAPI';
import type { UserDetail } from '../../api/models/UserDetail';
import { getCookieValue } from '../../utils/cookies';
import { setSessionExpiryInProgress } from '../../app/components/UnsavedChangesFormPrompt';

// Context headers function
const getContextHeaders = (queryClient: QueryClient) => {
  const user = queryClient.getQueryData<UserDetail>(['user', 'detail', 'current']);

  if (!user) return {};

  let contextType: string = '';
  let contextId: string = '';
  let contextMemberId: string = '';
  if (user.user_type == 'provider') {
    contextType = 'provider_id';
    contextId = user.provider_id;
    contextMemberId = user.provider_id;
  } else if (user.user_type == 'healthcare_entity_rep') {
    contextType = 'healthcare_entity_id';
    contextId = user.healthcare_entity_id;
    contextMemberId = user.healthcare_entity_rep_id;
  } else if (user.user_type == 'staff') {
    contextType = 'user_id';
    contextId = user.id;
    contextMemberId = user.id;
  }

  return {
    'SIH-Context-Type': contextType,
    'SIH-Context-ID': contextId,
    'SIH-Context-Member-ID': contextMemberId,
  };
};

let queryClientInstance: QueryClient | null = null;

export const setQueryClientInstance = (client: QueryClient) => {
  queryClientInstance = client;
};

export const api = axios.create({
  baseURL: window.APPLICATION_HOST,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface SessionExpiredHandler {
  handleSessionExpired: (isAuthError?: boolean) => void;
}

let sessionHandler: SessionExpiredHandler | null = null;

export const setSessionHandler = (handler: SessionExpiredHandler) => {
  sessionHandler = handler;
};

api.interceptors.request.use((config) => {
  const csrftoken = getCookieValue('csrftoken');
  if (csrftoken) {
    config.headers['X-CSRFTOKEN'] = csrftoken;
  }
  if (queryClientInstance) {
    const contextHeaders = getContextHeaders(queryClientInstance);

    config.headers = {
      ...config.headers,
      ...contextHeaders,
    } as AxiosHeaders;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response, // Pass through successful responses
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      const isAuthenticationError =
        error.response?.data?.detail === 'Authentication credentials were not provided.' ||
        error.response?.data?.detail === 'Invalid token.' ||
        error.response?.data?.detail === 'Session has expired.' ||
        error.response?.status === 401;

      if (isAuthenticationError && sessionHandler) {
        setSessionExpiryInProgress(true);
        sessionHandler.handleSessionExpired(true);
      }
    }

    return Promise.reject(error);
  }
);

export function isDefined<T>(value: T | null | undefined): value is Exclude<T, null | undefined> {
  return value !== undefined && value !== null;
}

export const isString = (value: any): value is string => {
  return typeof value === 'string';
};

export const isStringWithValue = (value: any): value is string => {
  return isString(value) && value !== '';
};

export const isBlob = (value: any): value is Blob => {
  return (
    typeof value === 'object' &&
    typeof value.type === 'string' &&
    typeof value.stream === 'function' &&
    typeof value.arrayBuffer === 'function' &&
    typeof value.constructor === 'function' &&
    typeof value.constructor.name === 'string' &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

export const isFormData = (value: any): value is FormData => {
  return value instanceof FormData;
};

export const isSuccess = (status: number): boolean => {
  return status >= 200 && status < 300;
};

export const base64 = (str: string): string => {
  try {
    return btoa(str);
  } catch (err) {
    // @ts-ignore
    return Buffer.from(str).toString('base64');
  }
};

export const getQueryString = (params: Record<string, any>): string => {
  const qs: string[] = [];

  const append = (key: string, value: any) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          process(key, v);
        });
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          process(`${key}[${k}]`, v);
        });
      } else {
        append(key, value);
      }
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  if (qs.length > 0) {
    return `?${qs.join('&')}`;
  }

  return '';
};

const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
  const encoder = config.ENCODE_PATH || encodeURI;

  const path = options.url
    .replace('{api-version}', config.VERSION)
    .replace(/{(.*?)}/g, (substring: string, group: string) => {
      if (options.path?.hasOwnProperty(group)) {
        return encoder(String(options.path[group]));
      }
      return substring;
    });

  const url = `${config.BASE}${path}`;
  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

export const getFormData = (options: ApiRequestOptions): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: any) => {
      if (isString(value) || isBlob(value)) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    };

    Object.entries(options.formData)
      .filter(([_, value]) => isDefined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => process(key, v));
        } else {
          process(key, value);
        }
      });

    return formData;
  }
  return undefined;
};

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

export async function resolve<T>(
  options: ApiRequestOptions,
  resolver?: T | Resolver<T>
): Promise<T | undefined> {
  if (typeof resolver === 'function') {
    return (resolver as Resolver<T>)(options);
  }
  return resolver;
}

export const getHeaders = async (
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  formData?: FormData
): Promise<Record<string, string>> => {
  const token = await resolve(options, config.TOKEN);
  const username = await resolve(options, config.USERNAME);
  const password = await resolve(options, config.PASSWORD);
  const additionalHeaders = await resolve(options, config.HEADERS);
  const formHeaders = (typeof formData?.getHeaders === 'function' && formData?.getHeaders()) || {};

  const headers = Object.entries({
    Accept: 'application/json',
    ...additionalHeaders,
    ...options.headers,
    ...formHeaders,
  })
    .filter(([_, value]) => isDefined(value))
    .reduce(
      (headers, [key, value]) => ({
        ...headers,
        [key]: String(value),
      }),
      {} as Record<string, string>
    );

  if (isStringWithValue(token)) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (isStringWithValue(username) && isStringWithValue(password)) {
    const credentials = base64(`${username}:${password}`);
    headers['Authorization'] = `Basic ${credentials}`;
  }

  if (options.body) {
    if (options.mediaType) {
      headers['Content-Type'] = options.mediaType;
    } else if (isBlob(options.body)) {
      headers['Content-Type'] = options.body.type || 'application/octet-stream';
    } else if (isString(options.body)) {
      headers['Content-Type'] = 'text/plain';
    } else if (!isFormData(options.body)) {
      headers['Content-Type'] = 'application/json';
    }
  }

  return headers;
};

export const getRequestBody = (options: ApiRequestOptions): any => {
  if (options.body) {
    return options.body;
  }
  return undefined;
};

export async function sendRequest<T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance
): Promise<AxiosResponse<T>> {
  const source = axios.CancelToken.source();

  const requestConfig: AxiosRequestConfig = {
    url,
    headers,
    data: body ?? formData,
    method: options.method,
    withCredentials: config.WITH_CREDENTIALS,
    cancelToken: source.token,
  };

  onCancel(() => source.cancel('The user aborted a request.'));

  try {
    return await axiosClient.request(requestConfig);
  } catch (error) {
    const axiosError = error as AxiosError<T>;
    if (axiosError.response) {
      return axiosError.response;
    }
    throw error;
  }
}

export const getResponseHeader = (
  response: AxiosResponse<any>,
  responseHeader?: string
): string | undefined => {
  if (responseHeader) {
    const content = response.headers[responseHeader];
    if (isString(content)) {
      return content;
    }
  }
  return undefined;
};

export const getResponseBody = (response: AxiosResponse<any>): any => {
  if (response.status !== 204) {
    return response.data;
  }
  return undefined;
};

export const catchErrorCodes = (options: ApiRequestOptions, result: ApiResult): void => {
  const errors: Record<number, string> = {
    400: 'Bad Request',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    ...options.errors,
  };

  // Skip throwing errors for auth issues - let the interceptor handle them
  if (result.status === 401 || result.status === 403) {
    const isAuthenticationError =
      result.body?.detail === 'Authentication credentials were not provided.' ||
      result.body?.detail === 'Invalid token.' ||
      result.body?.detail === 'Session has expired.' ||
      result.body?.error === 'Permission denied.' ||
      result.status === 401;

    if (isAuthenticationError) {
      // Still throw the error so mutations fail properly
      // The interceptor will handle the redirect separately
      throw new ApiError(options, result, 'Authentication required');
    }
  }

  const error = errors[result.status];
  if (error) {
    throw new ApiError(options, result, error);
  }

  if (!result.ok) {
    const errorStatus = result.status ?? 'unknown';
    const errorStatusText = result.statusText ?? 'unknown';
    const errorBody = (() => {
      try {
        return JSON.stringify(result.body, null, 2);
      } catch (e) {
        return undefined;
      }
    })();

    throw new ApiError(
      options,
      result,
      `Generic Error: status: ${errorStatus}; status text: ${errorStatusText}; body: ${errorBody}`
    );
  }
};

function getAxiosClient() {
  // If we need to differentiate between public and private API at some point in the future,
  // place logic here to determine which client to use.  This was used when jwts were implemented,
  // but complexity is being removed until needed again.
  return api;
}

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @param axiosClient The axios client instance to use
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export function request<T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  axiosClient?: AxiosInstance
): CancelablePromise<T> {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    try {
      const url = getUrl(config, options);
      const formData = getFormData(options);
      const body = getRequestBody(options);
      const headers = await getHeaders(config, options, formData);

      if (!onCancel.isCancelled) {
        const response = await sendRequest<T>(
          config,
          options,
          url,
          body,
          formData,
          headers,
          onCancel,
          axiosClient || getAxiosClient()
        );
        const responseBody = getResponseBody(response);
        const responseHeader = getResponseHeader(response, options.responseHeader);

        const result: ApiResult = {
          url,
          ok: isSuccess(response.status),
          status: response.status,
          statusText: response.statusText,
          body: responseHeader ?? responseBody,
        };

        catchErrorCodes(options, result);

        if (options.responseHeader) {
          resolve(result.body);
        } else {
          // If we want the full result (including status), we could return it here
          // but that would break all existing calls.
          // Instead, let's attach the status to the body if it's an object
          if (
            typeof result.body === 'object' &&
            result.body !== null &&
            !Array.isArray(result.body)
          ) {
            Object.defineProperty(result.body, '$responseStatus', {
              value: result.status,
              enumerable: false,
              writable: false,
              configurable: true,
            });
          }
          resolve(result.body);
        }
      }
    } catch (error) {
      reject(error);
    }
  });
}
